import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, catchError, forkJoin, from, map, Observable, of, switchMap, tap} from 'rxjs';
import {Store} from '@ngrx/store';
import {SecureStorage} from '@aparajita/capacitor-secure-storage';
import {Authentication} from '../authentication.model';
import {AuthenticationApiService} from './authentication-api.service';
import {loginSuccess} from '../store/authentication.actions';
import {GlobalState} from '../../../store/store.model';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private readonly _store = inject(Store<GlobalState>);
  private readonly _userKey = 'user';
  private readonly _authenticationApiService = inject(AuthenticationApiService);
  private _canAccessValidation$ = new BehaviorSubject<boolean>(false);


  constructor() {
  }

  public initAuthFlow(): Observable<void | Authentication | null> {
    return this._getLastAccessedUser().pipe(
      switchMap(userId => userId ? this._handleExistingUser(userId) : this.clearAllStorage())
    );
  }

  public clearAllStorage(): Observable<void | null> {
    return from(SecureStorage.clear()).pipe(catchError(err => {
      console.error('Errore durante la pulizia dello storage:', err);
      return of(null);
    }));
  }

  public finalizeLogin(authenticationData: Authentication, skipDispatch?: boolean): Observable<Authentication> {
    const userId = String(authenticationData.userId);
    const keysToStore = [
      {key: this._userKey, value: userId},
      {key: `${userId}.sessionId`, value: String(authenticationData?.sessionId)},
      {key: `${userId}.userId`, value: String(authenticationData?.userId)},
      {key: `${userId}.expiresAt`, value: String(authenticationData?.expiresAt)}
    ];
    const storageOperations = keysToStore.map(item => this._secureStorageSet(item.key, item.value));

    return forkJoin(storageOperations).pipe(
      map(() => authenticationData)
    );
  }

  private _handleExistingUser(userId: number): Observable<void | Authentication | null> {
    return this._isTokenExpired(userId).pipe(
      switchMap(expired => expired ? this.clearAllStorage() : this._refreshOrReauthenticate(userId))
    );
  }

  private _refreshOrReauthenticate(userId: number): Observable<Authentication | void | null> {
    return this._refreshSession(userId).pipe(
      switchMap(authenticationData =>
        authenticationData ?
          this.finalizeLogin(authenticationData).pipe(
            tap(() => this._store.dispatch(loginSuccess({response: authenticationData})))
          ) : this.clearAllStorage()
      )
    );
  }

  private _refreshSession(userId: number): Observable<Authentication | null> {
    this._secureStorageGet(`${userId}.sessionId`).pipe().subscribe();
    return this._secureStorageGet(`${userId}.sessionId`).pipe(
      switchMap(sessionId =>
        sessionId ?
          this._authenticationApiService.refreshSession(sessionId).pipe(
            map(response => response?.sessionId ? {
              userId: response?.userId,
              sessionId: response?.sessionId,
              name: response?.name,
              avatar: response?.avatar,
              expiresAt: response?.expiresAt
            } : null)
          ) : of(null)
      )
    );
  }

  _getLastAccessedUser(): Observable<number | null> {
    return this._secureStorageGet(this._userKey).pipe(
      map(user => user ? user : null)
    );
  }

  private _isTokenExpired(userId: number): Observable<boolean> {
    return this._secureStorageGet(`${userId}.expiresAt`).pipe(
      map(expiresAt => {
        if (!expiresAt || expiresAt === 'undefined') {
          return true;
        }
        const expiryDate = new Date(Number(expiresAt) * 1000);
        const now = new Date();
        return now > expiryDate;
      })
    );
  }

  private _secureStorageGet(key: string): Observable<any> {
    return from(SecureStorage.get(key)).pipe(
      catchError(err => {
        console.error(`Errore nella lettura della chiave ${key}:`, err);
        return of(null);
      })
    );
  }

  private _secureStorageSet(key: string, value: string): Observable<void | null> {
    return from(SecureStorage.set(key, value)).pipe(
      catchError(err => {
        console.error(`Errore nel salvataggio della chiave ${key}:`, err);
        return of(null);
      })
    );
  }

  allowValidationAccess() {
    this._canAccessValidation$.next(true);
  }

  hasValidationAccess$(): Observable<boolean> {
    return this._canAccessValidation$.asObservable();
  }

  resetValidationAccess() {
    this._canAccessValidation$.next(false);
  }
}
